import { Link } from 'wouter';
import { CtaBand, Hero, Section } from '../components/ui/primitives';
import { usePageMeta } from '../lib/seo';

/**
 * About. Baseline §3 — 5 sections: navy hero (96px 0) → white "Local. Responsive.
 * School-Ready." → muted "Who We Serve" (4 cards) → white "Our Values" (3 numbered)
 * → navy "Partner with Mopani Suppliers" CTA.
 *
 * Copy preserved verbatim from production (P-13).
 */

const AUDIENCES = [
  { title: 'Schools', body: 'Primary and secondary schools requiring bulk hygiene supplies.' },
  { title: 'Clinics', body: 'Local healthcare facilities needing professional sanitation products.' },
  { title: 'Gov Offices', body: 'Municipal and departmental offices across the district.' },
  { title: 'Community', body: 'Local NGOs and community organizations.' },
] as const;

const VALUES = [
  {
    n: '01',
    title: 'Integrity in Pricing',
    body: 'We offer competitive, transparent pricing on quotes. No hidden fees, no last-minute surprises. We respect institutional budgets.',
  },
  {
    n: '02',
    title: 'Compliance First',
    body: "We maintain strict adherence to CSD, SARS, and B-BBEE requirements to ensure our clients' procurement processes are seamless.",
  },
  {
    n: '03',
    title: 'Local Growth',
    body: 'As a local enterprise, we are invested in the Mopani region and the institutions that serve it.',
  },
] as const;

export function About() {
  usePageMeta({
    title: 'About Us',
    description:
      'Mopani Suppliers is a Phalaborwa-based B2B procurement partner supplying cleaning and hygiene products to schools, clinics and government offices. Rooted in the community, committed to service.',
    path: '/about',
  });

  return (
    <>
      <Hero
        title="About Mopani Suppliers"
        subtitle="Rooted in the community. Committed to service."
        padding="py-24"
      />

      <Section variant="white" title="Local. Responsive. School-Ready.">
        <div className="max-w-3xl space-y-6 text-lg text-charcoal/80">
          <p>
            Based in Phalaborwa, Mopani Suppliers was established with a singular mission: to
            provide reliable, professional-grade cleaning and hygiene products to the institutions
            that form the backbone of our community.
          </p>
          <p>
            We are not a retail store. We are a dedicated B2B procurement partner. We understand
            that school bursars, facility managers, and local government buyers need more than just
            products—they need accurate quotes, compliant documentation, and dependable delivery.
          </p>
        </div>
      </Section>

      <Section
        variant="muted"
        title="Who We Serve"
        intro="Providing essential supplies to the organizations that keep our region running."
      >
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {AUDIENCES.map((a) => (
            <div key={a.title} className="card p-6">
              <h3 className="font-heading text-xl font-bold text-navy">{a.title}</h3>
              <p className="mt-3 text-charcoal/80">{a.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section variant="white" title="Our Values">
        <div className="grid gap-10 md:grid-cols-3">
          {VALUES.map((v) => (
            <div key={v.n}>
              <p className="font-heading text-3xl font-bold text-green-cta" aria-hidden="true">
                {v.n}
              </p>
              <h3 className="mt-3 font-heading text-xl font-bold text-navy">{v.title}</h3>
              <p className="mt-3 text-charcoal/80">{v.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <CtaBand
        title="Partner with Mopani Suppliers"
        body="Let us prepare a formal quotation for your institution's next order."
        action={
          <Link href="/quote" className="btn btn-primary btn-band">
            Request a Quote
          </Link>
        }
      />
    </>
  );
}
